import Interactable from '../../../Component/Interactable';
import CardComponent from '../../../Component/Level/Card';
import Renderable from '../../../Component/Renderable';
import TransformComponent from '../../../Component/Transform';
import GameObject from '../../../GameObject/GameObject';
import Animator from '../../../Prototype/Animator';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { ICardStateTaskData } from '../../../State/Card/CardReducer';
import CardStateHelper from '../../../State/Card/CardStateHelper';
import { CardTask } from '../../../State/Card/CardTasks';
import CardAnimationTask from '../../../Utility/Task/CardAnimationTask';
import { AnimationClipEvent } from '../../Animation/AnimationClip';
import { StartAnimation, StopAnimation } from '../../Animation/AnimationHelper';
import { AnimationSystemEvent } from '../../Animation/AnimationSystem';
import {
    AUDIO_BOOSTER_SHUFFLE,
    AUDIO_BOOSTER_UNDO,
    AUDIO_CARD_DEAL_CARDS,
    AUDIO_GOLD_CARD_COLLECT,
    AUDIO_HAZARD_ICE,
    AUDIO_WILDCARD_ANCHOR,
    AUDIO_WILDCARD_BOOM,
    AUDIO_WILDCARD_PLUS_THREE_DECK,
    AUDIO_WILDCARD_ZAP,
} from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { StartAudio } from '../../Audio/AudioHelper';
import CardSystem, { getCardHandler } from '../CardSystem';
import BaseCardHandler from '../Handler/BaseCardHandler';
import StreakTask from './StreakTask';

export default class CardStateAnimationHelper {
    public static fold(system: CardSystem, taskData: ICardStateTaskData): void {
        const card = system.getCardByID(taskData.cards[0]);

        // Re-add to the stack to make sure it's in front.
        system.stackContainer.addChild(card);

        getCardHandler(card).setCardFaceUp(false);
        card.getComponent(TransformComponent).position.set(-150, 0);

        // Stop any animations (prevent weird scale issues with "cardPulse").
        card.scene.emit(AnimationSystemEvent.STOP, card);

        const task = new CardAnimationTask(
            taskData,
            system,
            'CardFold',
            card,
            null,
            true
        );

        const matches = CardStateHelper.getCardMatches(
            system.state.getPreviousState()
        );

        if (matches.length > 0) {
            StartAnimation(system.getCardByID(matches[0]), 'CardPulse');
        }

        system.taskQueue.queue(task);
    }

    public static match(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const card = system.getCardByID(taskData.cards[0]);

        const task = new CardAnimationTask(
            taskData,
            system,
            'CardMatch',
            card,
            {
                cardScore: taskData.cardScore,
            },
            true
        );

        system.taskQueue.queue(task);

        if (card.getComponent(CardComponent).type === CardType.gold) {
            task.animationClip.on(AnimationClipEvent.COMPLETE, () => {
                if (
                    card.getChildByName('animationContainer') &&
                    card
                        .getChildByName('animationContainer')
                        .getChildByName('goldContainer')
                ) {
                    StopAnimation(
                        card
                            .getChildByName('animationContainer')
                            .getChildByName('goldContainer')
                    );
                    StartAnimation(
                        card
                            .getChildByName('animationContainer')
                            .getChildByName('goldContainer'),
                        'DragonbonesDefault',
                        {
                            animationName: 'GoldCard_Collect',
                        }
                    );

                    StartAudio(
                        system.scene,
                        AUDIO_GOLD_CARD_COLLECT,
                        AudioTrackType.SFX
                    );
                }
            });
        }

        system.taskQueue.queue(new StreakTask());
    }

    public static flip(system: CardSystem, taskData: ICardStateTaskData): void {
        for (let cardID of taskData.cards) {
            const card = system.getCardByID(cardID);
            getCardHandler(card).setCardFaceUp(false);
            system.taskQueue.queue(
                new CardAnimationTask(
                    taskData,
                    system,
                    'CardFlip',
                    card,
                    {
                        faceUp: true,
                        rotation: card.getComponent(CardComponent).rotation,
                    },
                    true
                )
            );
        }
    }

    public static wiggle(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const card = system.getCardByID(taskData.cards[0]);
        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardWiggle',
                card,
                null,
                true
            )
        );
    }

    public static zap(system: CardSystem, taskData: ICardStateTaskData): void {
        StartAudio(system.scene, AUDIO_WILDCARD_ZAP, AudioTrackType.SFX);

        const sourceCard: BaseCard = system.getCardByID(taskData.cards[0]);
        sourceCard.getComponent(Renderable).visible = true;
        sourceCard.score = taskData.cardsScore[0];

        const targetCards: BaseCard[] = [];
        for (let i = 1; i < taskData.cards.length; i++) {
            const card: BaseCard = system.getCardByID(taskData.cards[i]);
            card.getComponent(Renderable).visible = true;
            card.score = taskData.cardsScore[i];
            system.animator.addChild(card);
            targetCards.push(card);
        }

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardZap',
                system.animator,
                {
                    sourceCard: sourceCard,
                    targetCards: targetCards,
                }
            )
        );
    }

    public static boom(system: CardSystem, taskData: ICardStateTaskData): void {
        StartAudio(system.scene, AUDIO_WILDCARD_BOOM, AudioTrackType.SFX);

        const sourceCard: BaseCard = system.getCardByID(taskData.cards[0]);
        sourceCard.getComponent(Renderable).visible = true;
        sourceCard.score = taskData.cardsScore[0];

        const targetCards: BaseCard[] = [];
        for (let i = 1; i < taskData.cards.length; i++) {
            const card: BaseCard = system.getCardByID(taskData.cards[i]);
            card.score = taskData.cardsScore[i];
            targetCards.push(card);
        }

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardExplosion',
                system.animator,
                {
                    sourceCard: sourceCard,
                    targetCards: targetCards,
                }
            )
        );
    }

    public static bomb(system: CardSystem, taskData: ICardStateTaskData): void {
        const sourceCard = system.getCardByID(taskData.cards[0]);
        sourceCard.getComponent(Renderable).visible = true;

        const targetCards: BaseCard[] = [];
        for (let i = 1; i < taskData.cards.length; i++) {
            const card: BaseCard = system.getCardByID(taskData.cards[i]);
            card.getComponent(Renderable).visible = false;
            targetCards.push(card);
        }

        const explosionAnimator = GameObject.Instantiate(Animator);
        system.animator.addChild(explosionAnimator);

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardBombExplosion',
                explosionAnimator,
                {
                    sourceCard: sourceCard,
                    targetCards: targetCards,
                },
                true
            )
        );
    }

    public static ice(system: CardSystem, taskData: ICardStateTaskData): void {
        StartAudio(system.scene, AUDIO_HAZARD_ICE, AudioTrackType.SFX);

        const discard = system.getCardByID(taskData.discard);
        discard.getComponent(Renderable).visible = true;

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardIceBreak',
                system.getCardByID(taskData.source),
                {
                    discard: discard,
                    cardScore: taskData.cardScore,
                },
                true
            )
        );
    }

    public static crab(system: CardSystem, taskData: ICardStateTaskData): void {
        const sourceCard: BaseCard = system.getCardByID(taskData.cards[0]);
        sourceCard.score = taskData.cardsScore[0];

        const targetCards: BaseCard[] = [];
        for (let i = 1; i < taskData.cards.length; i++) {
            const card: BaseCard = system.getCardByID(taskData.cards[i]);
            if (i < taskData.cardsScore.length) {
                card.score = taskData.cardsScore[i];
            }
            targetCards.push(card);
        }

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardCrabs',
                system.animator,
                {
                    sourceCard: sourceCard,
                    targetCards: targetCards,
                }
            )
        );
    }

    public static key(system: CardSystem, taskData: ICardStateTaskData): void {
        const sourceCard: BaseCard = system.getCardByID(taskData.cards[0]);
        sourceCard.getComponent(Renderable).visible = true;
        sourceCard.score = taskData.cardsScore[0];
        system.animator.addChild(sourceCard);

        const targetCards: BaseCard[] = [];
        for (let i = 1; i < taskData.cards.length; i++) {
            const card: BaseCard = system.getCardByID(taskData.cards[i]);
            card.getComponent(Renderable).visible = true;
            card.score = taskData.cardsScore[i];
            system.animator.addChild(card);
            targetCards.push(card);
        }

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardKeyUnlock',
                system.animator,
                {
                    sourceCard: sourceCard,
                    targetCards: targetCards,
                }
            )
        );
    }

    public static plusThree(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const sourceCard = system.getCardByID(taskData.cards[0]);
        sourceCard.score = taskData.cardsScore[0];

        // Hide source card.
        sourceCard.getComponent(Renderable).visible = false;
        sourceCard.getComponent(Interactable).enabled = false;

        const plusThreeAnimator = GameObject.Instantiate(Animator);
        system.animator.addChild(plusThreeAnimator);

        const targetCards: BaseCard[] = [];
        for (let i = 1; i < taskData.cards.length; i++) {
            const card: BaseCard = system.getCardByID(taskData.cards[i]);
            card.score = taskData.cardsScore[i];
            targetCards.push(card);

            // Hide target cards until effect complete.
            card.getComponent(Renderable).visible = false;
            card.getComponent(Interactable).enabled = false;
            plusThreeAnimator.addChild(card);
        }

        const task = new CardAnimationTask(
            taskData,
            system,
            'CardPlusThree',
            plusThreeAnimator,
            {
                sourceCard: sourceCard,
                targetCards: targetCards,
                cardsContainer: system.cardsContainer,
                stackContainer: system.stackContainer,
            },
            true
        );

        task.animationClip.on('PlusThreeCardAdded', card => {
            StartAudio(
                system.scene,
                AUDIO_WILDCARD_PLUS_THREE_DECK,
                AudioTrackType.SFX
            );
        });
        system.taskQueue.queue(task);
    }

    public static tornado(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const sourceCard = system.getCardByID(taskData.cards[0]);
        sourceCard.getComponent(Renderable).visible = true;
        sourceCard.score = taskData.cardsScore[0];
        system.animator.addChild(sourceCard);

        const targets: any[] = taskData.targets.map(
            (target: any, index: number) => {
                const card: BaseCard = system.getCardByID(target.cardID);
                card.score = taskData.cardsScore[index + 1];
                return {
                    ...target,
                    card: card,
                };
            }
        );

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardTornado',
                system.animator,
                {
                    sourceCard: sourceCard,
                    targets: targets,
                }
            )
        );
    }

    public static fogEnd(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const card = system.getCardByID(taskData.cards[0]);
        const fogContainer = card.find('modifierContainer/fog/fogContainer');

        StartAnimation(fogContainer, 'DragonbonesDefault', {
            animationName: 'fog_end',
        });
    }

    public static plusFive(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        StartAudio(system.scene, AUDIO_CARD_DEAL_CARDS, AudioTrackType.SFX);

        const targetCards = taskData.cards.map((targetCardID: string) =>
            system.getCardByID(targetCardID)
        );

        // Hide target cards initially.
        for (let card of targetCards) {
            card.getComponent(Renderable).visible = false;
        }

        const task = new CardAnimationTask(
            taskData,
            system,
            'CardPlusFive',
            system.animator,
            {
                targetCards: targetCards,
                stackContainer: system.stackContainer,
            }
        );

        system.taskQueue.queue(task);
    }

    public static plusStreak(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const targetCards = taskData.cards.map((targetCardID: string) => {
            const card = system.getCardByID(targetCardID);
            card.getComponent(Renderable).visible = false;

            return card;
        });

        const streakAnimator = GameObject.Instantiate(Animator);
        system.animator.addChild(streakAnimator);

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'CardPlusStreak',
                streakAnimator,
                {
                    targetCards: targetCards,
                    stackContainer: system.stackContainer,
                },
                true
            )
        );
    }

    public static anchor(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        StartAudio(system.scene, AUDIO_WILDCARD_ANCHOR, AudioTrackType.SFX);

        const sourceCard = system.getCardByID(taskData.cards[0]);
        sourceCard.getComponent(Renderable).visible = true;
        sourceCard.score = taskData.cardsScore[0];
        system.animator.addChild(sourceCard);

        system.taskQueue.queue(
            new CardAnimationTask(taskData, system, 'CardAnchor', sourceCard, {
                sourceCard: sourceCard,
            })
        );
    }

    public static undoMatch(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        StartAudio(system.scene, AUDIO_BOOSTER_UNDO, AudioTrackType.SFX);
        const card = system.getCardByID(taskData.cards[0]);

        StopAnimation(card);

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'UndoMatch',
                card,
                undefined,
                true
            )
        );
    }

    public static undoFold(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        StartAudio(system.scene, AUDIO_BOOSTER_UNDO, AudioTrackType.SFX);

        const card = system.getCardByID(taskData.cards[0]);

        // Re-position card.
        system.stackContainer.addChild(card);
        card.getComponent(TransformComponent).position.set(0, 0);

        // Set face-up.
        const handler: BaseCardHandler = getCardHandler(card);
        handler.setCardFaceUp(true);

        card.scene.emit(AnimationSystemEvent.STOP, card);

        const task = new CardAnimationTask(
            taskData,
            system,
            'UndoFold',
            card,
            undefined,
            true
        );
        system.taskQueue.queue(task);
    }

    public static shuffle(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        StartAudio(system.scene, AUDIO_BOOSTER_SHUFFLE, AudioTrackType.SFX);
        const targets: any[] = taskData.targets.map((target: any) => {
            return {
                ...target,
                card: system.getCardByID(target.cardID),
            };
        });

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'BoosterShuffle',
                system.animator,
                {
                    targets: targets,
                }
            )
        );
    }

    public static tnt(system: CardSystem, taskData: ICardStateTaskData): void {
        const targets: {
            card: BaseCard;
            score: number;
        }[] = taskData.targets.map((target: any) => {
            return {
                card: system.getCardByID(target.cardID),
                score: target.score,
            };
        });

        for (let target of targets) {
            system.animator.addChild(target.card);
            target.card.getComponent(Renderable).visible = true;
        }

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'BoosterTNT',
                system.animator,
                {
                    targets: targets,
                }
            )
        );
    }

    public static joker(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        const jokerCard = system.getCardByID(taskData.cards[0]);
        const transform = jokerCard.getComponent(TransformComponent);

        system.stackContainer.addChild(jokerCard);
        jokerCard.getComponent(Renderable).visible = true;
        transform.scale.set(0.05, 0.05);
        transform.position.set(0, 0);

        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'BoosterJoker',
                system.animator,
                {
                    jokerCard: jokerCard,
                },
                true
            )
        );
    }

    public static bullsEye(
        system: CardSystem,
        taskData: ICardStateTaskData
    ): void {
        if (!taskData.cards || taskData.cards.length <= 0) return;

        const card = system.getCardByID(taskData.cards[0]);

        card.getComponent(Renderable).visible = true;
        system.taskQueue.queue(
            new CardAnimationTask(
                taskData,
                system,
                'BoosterBullsEye',
                system.animator,
                {
                    card: card,
                    score: taskData.score,
                }
            )
        );
    }

    public static handleTask(system: CardSystem, taskData: ICardStateTaskData) {
        switch (taskData.task) {
            case CardTask.FOLD:
                return CardStateAnimationHelper.fold(system, taskData);
            case CardTask.MATCH:
                return CardStateAnimationHelper.match(system, taskData);
            case CardTask.FLIP:
                return CardStateAnimationHelper.flip(system, taskData);
            case CardTask.WIGGLE:
                return CardStateAnimationHelper.wiggle(system, taskData);
            case CardTask.ZAP:
                return CardStateAnimationHelper.zap(system, taskData);
            case CardTask.BOOM:
                return CardStateAnimationHelper.boom(system, taskData);
            case CardTask.BOMB:
                return CardStateAnimationHelper.bomb(system, taskData);
            case CardTask.ICE:
                return CardStateAnimationHelper.ice(system, taskData);
            case CardTask.CRAB:
                return CardStateAnimationHelper.crab(system, taskData);
            case CardTask.KEY:
                return CardStateAnimationHelper.key(system, taskData);
            case CardTask.PLUS_THREE:
                return CardStateAnimationHelper.plusThree(system, taskData);
            case CardTask.TORNADO:
                return CardStateAnimationHelper.tornado(system, taskData);
            case CardTask.FOG_END:
                return CardStateAnimationHelper.fogEnd(system, taskData);
            case CardTask.ANCHOR:
                return CardStateAnimationHelper.anchor(system, taskData);
            case CardTask.PLUS_FIVE:
                return CardStateAnimationHelper.plusFive(system, taskData);
            case CardTask.PLUS_STREAK:
                return CardStateAnimationHelper.plusStreak(system, taskData);
            case CardTask.UNDO_MATCH:
                return CardStateAnimationHelper.undoMatch(system, taskData);
            case CardTask.UNDO_FOLD:
                return CardStateAnimationHelper.undoFold(system, taskData);
            case CardTask.SHUFFLE:
                return CardStateAnimationHelper.shuffle(system, taskData);
            case CardTask.TNT:
                return CardStateAnimationHelper.tnt(system, taskData);
            case CardTask.BULLS_EYE:
                return CardStateAnimationHelper.bullsEye(system, taskData);
            case CardTask.JOKER:
                return CardStateAnimationHelper.joker(system, taskData);
        }
    }
}
